import Header from '../Header/Header';
import React, { useState, useEffect, useCallback } from 'react';
import './index.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { Select, ConfirmationPopup, ICONS, SignaturePad } from '@ohif/ui';
import { Formik, Form, ErrorMessage } from 'formik';
import { hospitalFormSchema } from './schema';
import { SuccessModal, Loader } from '@ohif/ui';
import { apiRequest } from '@custom/Utils/config/apiRequest';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import { _commonUrls, _hospitalUrls } from '@custom/Utils/constants/apiUrls';
import { customStyle } from '@custom/Utils/utils';
import updateProfileImage from '@custom/rtk-apis/report/updateProfileImage';
import { SignatureDropzone } from '@custom/Profile';
import { HospitalDetails } from '@custom/Utils/constants/interfaces';
import { useTheme } from '../../ui/src/contextProviders';
import mastersuperadminchangePasswordApi from '@custom/rtk-apis/masterSuperAdminProfile/changePassword';
import addSuperadminDetailsApi from '@custom/rtk-apis/masterSuperAdminProfile/addSuperadminDetail';
import getSuperadminDetailsApi from '@custom/rtk-apis/masterSuperAdminProfile/getSuperadminDetails';
import changePasswordApi from '@custom/rtk-apis/user/changePassword';
import hospitalDetailApi from '@custom/rtk-apis/hospital/hospitalDetails';

const defaultHospitalDetails: HospitalDetails = {
  _id: '',
  hospitalName: '',
  hospitalEmailId: '',
  address: '',
  zipCode: '',
  country: '',
  state: '',
  city: '',
  hfrId: '',
  website: '',
  contactNumberCountryCode: '',
  hospitalData: '',
  contactNumber: '',
  whatsappCountryCode: '',
  whatsapp: '',
  emergencyContactNumberCountryCode: '',
  emergencyContactNumber: '',
  noOfHealthCareProfessional: '',
  specialitites: '',
  sameAsContact: '',
  signature: '',
  telepathology: false,
  teleradiology: false,
  user: {
    _id: '',
    name: '',
    email: '',
    contactNumber: '',
    countryCode: '',
    whatsapp: '',
    whatsappCountryCode: '',
    sameAsContact: '',
    superAdminSameAsContact: '',
  },
};

const myProfileHospital = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();

  const [countryCode, setCountryCode] = useState(['+91']);
  const [countryList, setCountryList] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [specialitites, setSpecialitities] = useState([]);
  const [edit, setIsEdit] = useState(false);
  const [modal, setModal] = useState(false);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [hospitalDetails, setHospitalDetails] = useState<HospitalDetails>(defaultHospitalDetails);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [isHospitalSuperAdminEditing, setIsHospitalSuperAdminEditing] = useState(
    location?.state?.isHospitalSuperAdminEditing ?? false
  );
  const [folderName, setFolderName] = useState<string>('');
  const [isOpenSignaturePad, setOpenSignaturePad] = useState(false);
  const [profileImage, setProfileImage] = useState<string>('');
  const [signatureImage, setSignatureImage] = useState<string>('');

  const { hospitalData } = location?.state || {};
  const masterSuperadminChangePasswordData = useSelector((state: RootState) => state?.masterSuperAdminChangePasswordResponse);
  const imageResponse = useSelector((state: RootState) => state.updateProfileImageResponse);
  const hospitalDetailsData = useSelector((state: RootState) => state?.hospitalDetailsResponse);
  const addMasterSuperadminDetailsData = useSelector((state: RootState) => state?.addSuperadminDetailsResponse);
  const getSuperadminDetailsData = useSelector((state: RootState) => state?.getSuperadminDetailsResponse);

  const role = sessionStorage.getItem('role') || localStorage.getItem('role');
  const isSuperAdminMyProfile = role === 'Superadmin' && location.pathname.includes('/myprofile');

  const [formInitialValue, setFormInitialValue] = useState({
    hospitalName: '',
    hospitalEmailId: '',
    address: '',
    zipCode: '',
    country: '',
    state: '',
    city: '',
    hfrId: '',
    website: '',
    contactNumberCountryCode: '+91',
    contactNumber: '',
    whatsappCountryCode: '+91',
    whatsappNumber: '',
    emergencyContactNumberCountryCode: '+91',
    emergencyContactNumber: '',
    numberOfHealthcareProfessionals: '',
    specialitites: [],
    signature: '',
    superAdminName: '',
    superAdminEmail: '',
    superAdminContactNumber: '',
    superAdminContactNumberCountryCode: '+91',
    superAdminWhatsappNumber: '',
    superAdminWhatsappNumberCountryCode: '+91',
    sameAsContact: false,
    superAdminSameAsContact: false,
    telepathology: false,
    teleradiology: false,
  });

  const masterSuperadminName = sessionStorage.getItem('name');
  const masterSuperadminEmail = sessionStorage.getItem('email');

  const fetchCommonData = async () => {
    try {
      const commonData: any = await apiRequest({
        method: 'POST',
        endUrl: _commonUrls.list,
        body: {},
        headerType: 'json',
      });
      if (commonData?.status) {
        setCountryCode(commonData?.result?.countryCode);
        setCountryList(commonData?.result?.countryList);
        setSpecialitities(
          commonData?.result?.department?.map((item) =>
            item === 'Multi specality hospital'
              ? { value: 'Multispeciality Hospital', label: 'Multispeciality Hospital' }
              : { value: item, label: item }
          )
        );
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchStates = async (country, state, type) => {
    const response: any = await apiRequest({
      method: 'POST',
      endUrl: _commonUrls.statecity,
      body: { type: type, countryCode: country, stateCode: state },
      headerType: 'json',
    });
    if (response?.status) {
      if (type === 'state') {
        setStates(response.result);
      } else {
        setCities(response.result);
      }
    }
  };

  const onDrop = useCallback((acceptedFiles, setFieldValue) => {
    const file = acceptedFiles[0];
    uploadSignatureFile(file);
  }, []);

  const uploadSignatureFile = (file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFolderName('signature');
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folderName', 'signature');

        dispatch(updateProfileImage(formData))
          .unwrap()
          .then((result) => {
            console.log('File uploaded successfully:', result);
          })
          .catch((error) => {
            console.error('Error uploading file:', error);
          });
      };
      reader.readAsDataURL(file);
    }
  };

  const base64ToBlob = (base64, type = 'image/png') => {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type });
  };

  const saveSignature = (signatureData) => {
    setOpenSignaturePad(false);
    const blob = base64ToBlob(signatureData);
    const file = new File([blob], `signature_${Date.now()}.png`, { type: 'image/png' });
    uploadSignatureFile(file);
  };

  const openSignaturePad = () => {
    setOpenSignaturePad(true);
  };

  const handlePasswordChange = (key, value) => {
    setPassword({ ...password, [key]: value });
  };

  const handlePasswordSubmit = () => {
    setTitle('Update Password');
    setConfirmationText('Are you sure you want to update your password?');
    setConfirmationModal(true);
  };

  const confirmPasswordChange = async () => {
    const { currentPassword, newPassword, confirmPassword } = password;

    if (!currentPassword) {
      setErrorMessage('Current password is required.');
      return;
    }
    if (!newPassword) {
      setErrorMessage('New password is required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage('New password and confirm password do not match.');
      return;
    }

    setLoading(true);
    try {
      let payload;
      let response;

      if (role === 'Superadmin') {
        const superadminId = sessionStorage.getItem('userId');
        payload = {
          superadminId,
          currentPassword,
          newPassword,
          confirmPassword,
        };
        response = await dispatch(mastersuperadminchangePasswordApi(payload)).unwrap();
      } else {
        const hospitalId =
          hospitalData?.user?.hospitalId ||
          localStorage.getItem('hospitalId') ||
          sessionStorage.getItem('hospitalId');
        const userId =
          hospitalData?.user?._id ||
          localStorage.getItem('userId') ||
          sessionStorage.getItem('userId');
        payload = {
          hospitalId,
          userId,
          currentPassword,
          newPassword,
          confirmPassword,
        };
        response = await dispatch(changePasswordApi(payload));
      }

      if (role === 'Superadmin') {
        if (response?.status === 'SUCCESS') {
          setTitle('Password changed successfully.');
          setModal(true);
          setPassword({ currentPassword: '', newPassword: '', confirmPassword: '' });
          setErrorMessage('');
        } else {
          setErrorMessage(response?.msg || 'Failed to change password.');
        }
      } else {
        if (response?.meta?.requestStatus === 'fulfilled') {
          if (response?.payload?.status === 'SUCCESS') {
            setTitle('Password changed successfully.');
            setModal(true);
            setPassword({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setErrorMessage('');
          } else if (response?.payload?.status === 'ERROR') {
            setErrorMessage(response?.payload?.msg);
          }
        } else if (response?.error?.message) {
          setErrorMessage(response.error.message);
        }
      }
    } catch (error) {
      setErrorMessage(error.message || 'An error occurred while changing the password.');
    } finally {
      setLoading(false);
      setConfirmationModal(false);
    }
  };

  const handleSave = async (values, { resetForm }) => {
    setLoading(true);
    try {
      if (isSuperAdminMyProfile) {
        const superadminId = sessionStorage.getItem('userId');
        const payload = {
          _id: superadminId,
          name: values.superAdminName,
          contactNumber: values.superAdminContactNumber,
          whatsappNumber: values.superAdminWhatsappNumber,
        };

        const response = await dispatch(addSuperadminDetailsApi(payload)).unwrap();
        if (response?.status === 'SUCCESS') {
          setTitle('Superadmin details updated successfully.');
          setModal(true);
          setIsHospitalSuperAdminEditing(false);
          await dispatch(getSuperadminDetailsApi());
        } else {
          setErrorMessage(response?.msg || 'Failed to update superadmin details.');
        }
      } else {
        let obj = {};
        if (edit || isHospitalSuperAdminEditing) {
          obj = {
            _id: hospitalData?._id || hospitalDetails?._id,
            hospitalName: values?.hospitalName,
            hospitalEmailId: values?.hospitalEmailId,
            contactNumber: values?.contactNumber,
            address: values?.address,
            zipCode: values?.zipCode,
            country: values?.country,
            state: values?.state,
            city: values?.city,
            hfrId: values?.hfrId,
            whatsapp: values?.whatsappNumber,
            emergencyContactNumber: values?.emergencyContactNumber,
            contactNumberCountryCode: values?.contactNumberCountryCode,
            whatsappCountryCode: values?.whatsappCountryCode,
            emergencyContactNumberCountryCode: values?.emergencyContactNumberCountryCode,
            noOfHealthCareProfessional: values?.numberOfHealthcareProfessionals,
            specialitites: values?.specialitites,
            website: values?.website,
            sameAsContact: values?.sameAsContact,
            telepathology: values?.telepathology,
            teleradiology: values?.teleradiology,
            adminDetails: {
              _id: hospitalData?.user?._id || hospitalDetails?.user?._id,
              name: values?.superAdminName,
              email: values?.superAdminEmail,
              countryCode: values?.superAdminContactNumberCountryCode,
              contactNumber: values?.superAdminContactNumber,
              whatsappCountryCode: values?.superAdminWhatsappNumberCountryCode,
              whatsapp: values?.superAdminWhatsappNumber,
              superAdminSameAsContact: values?.superAdminSameAsContact,
              signature: imageResponse.result,
            },
          };
        } else {
          obj = {
            hospitalDetail: {
              hospitalName: values?.hospitalName,
              hospitalEmailId: values?.hospitalEmailId,
              contactNumber: values?.contactNumber,
              address: values?.address,
              zipCode: values?.zipCode,
              country: values?.country,
              state: values?.state,
              city: values?.city,
              hfrId: values?.hfrId,
              whatsapp: values?.whatsappNumber,
              emergencyContactNumber: values?.emergencyContactNumber,
              contactNumberCountryCode: values?.contactNumberCountryCode,
              whatsappCountryCode: values?.whatsappCountryCode,
              emergencyContactNumberCountryCode: values?.emergencyContactNumberCountryCode,
              noOfHealthCareProfessional: values?.numberOfHealthcareProfessionals,
              specialitites: values?.specialitites,
              website: values?.website,
              sameAsContact: values?.sameAsContact,
              telepathology: values?.telepathology,
              teleradiology: values?.teleradiology,
            },
            adminDetails: {
              name: values?.superAdminName,
              email: values?.superAdminEmail,
              countryCode: values?.superAdminContactNumberCountryCode,
              contactNumber: values?.superAdminContactNumber,
              whatsappCountryCode: values?.superAdminWhatsappNumberCountryCode,
              whatsapp: values?.superAdminWhatsappNumber,
              superAdminSameAsContact: values?.superAdminSameAsContact,
              signature: imageResponse.result,
            },
          };
        }

        const response = await apiRequest({
          method: 'POST',
          endUrl: edit || role === 'hospitalsuperadmin' ? _hospitalUrls.update : _hospitalUrls.add,
          headerType: 'json',
          body: obj,
        });
        if (response?.status) {
          if (role !== 'hospitalsuperadmin') {
            setTitle(response?.msg);
          } else {
            setTitle('Profile updated successfully.');
          }
          setModal(true);

          const hospitalId =
            sessionStorage.getItem('hospitalId') ||
            localStorage.getItem('hospitalId') ||
            hospitalData?.user?.hospitalId;
          if (hospitalId) {
            await dispatch(hospitalDetailApi({ hospitalId })).unwrap();
          }

          if (role !== 'hospitalsuperadmin') {
            setTimeout(() => {
              navigate('/hospital');
            }, 500);
          } else {
            setIsHospitalSuperAdminEditing(false);
          }
        }
      }
    } catch (err) {
      setErrorMessage('An error occurred while saving the details.');
    } finally {
      setLoading(false);
    }
    resetForm();
  };

  const handleCloseModal = () => {
    setModal(false);
  };

  const cancel = () => {
    setFormInitialValue({
      hospitalName: '',
      hospitalEmailId: '',
      address: '',
      zipCode: '',
      country: '',
      state: '',
      city: '',
      hfrId: '',
      website: '',
      contactNumberCountryCode: '+91',
      contactNumber: '',
      whatsappCountryCode: '+91',
      whatsappNumber: '',
      emergencyContactNumberCountryCode: '+91',
      emergencyContactNumber: '',
      numberOfHealthcareProfessionals: '',
      specialitites: [],
      signature: '',
      superAdminName: '',
      superAdminEmail: '',
      superAdminContactNumber: '',
      superAdminContactNumberCountryCode: '+91',
      superAdminWhatsappNumber: '',
      superAdminWhatsappNumberCountryCode: '+91',
      sameAsContact: false,
      superAdminSameAsContact: false,
      telepathology: false,
      teleradiology: false,
    });
    if (role === 'Superadmin') {
      dispatch(getSuperadminDetailsApi());
    }
    navigate('/myprofile');
  };

  useEffect(() => {
    if (role === 'Superadmin') {
      dispatch(getSuperadminDetailsApi());
    }
    fetchCommonData();
    if (edit || role === 'hospitalsuperadmin') {
      const hospitalId =
        sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId') || hospitalData?.user?.hospitalId;
      if (hospitalId) {
        dispatch(hospitalDetailApi({ hospitalId }));
      }
    }
  }, [dispatch, edit, role, hospitalData]);

  useEffect(() => {
    if (getSuperadminDetailsData?.status === 'SUCCESS' && role === 'Superadmin') {
      const details = getSuperadminDetailsData?.result;
      setFormInitialValue((prev) => ({
        ...prev,
        superAdminName: details?.name || '',
        superAdminEmail: details?.email || '',
        superAdminContactNumber: details?.contactNumber || '',
        superAdminContactNumberCountryCode: details?.countryCode || '+91',
        superAdminWhatsappNumber: details?.whatsappNumber || '',
        superAdminWhatsappNumberCountryCode: details?.whatsappCountryCode || '+91',
        superAdminSameAsContact:
          details?.contactNumber && details?.whatsappNumber && details?.contactNumber === details?.whatsappNumber,
      }));
      sessionStorage.setItem('name', details?.name || '');
      sessionStorage.setItem('email', details?.email || '');
    }
  }, [getSuperadminDetailsData, role]);

  useEffect(() => {
    if (hospitalDetailsData?.status === 'SUCCESS') {
      const details: any = hospitalDetailsData?.result;
      setHospitalDetails(details);
      if (role === 'hospitalsuperadmin') {
        updateFormInitialValue(details);
      }
      if (details?.country) {
        fetchStates(details?.country, '', 'state');
      }
      if (details?.country && details?.state) {
        fetchStates(details?.country, details?.state, 'city');
      }
    }
  }, [hospitalDetailsData, role]);

  useEffect(() => {
    if (hospitalData && Object.keys(hospitalData).length > 0) {
      updateFormInitialValue(hospitalData); // Update formInitialValue with hospitalData
      if (hospitalData?.country) {
        fetchStates(hospitalData?.country, '', 'state');
      }
      if (hospitalData?.country && hospitalData?.state) {
        fetchStates(hospitalData?.country, hospitalData?.state, 'city');
      }
      setIsEdit(true);
    }
  }, [hospitalData]);

  useEffect(() => {
    if (imageResponse?.status === 'SUCCESS') {
      if (folderName === 'profile') {
        setProfileImage(imageResponse?.result);
      } else {
        setSignatureImage(imageResponse?.result);
        setFormInitialValue((prevValue) => ({
          ...prevValue,
          signature: imageResponse?.result,
        }));
      }
    }
  }, [imageResponse]);

  const updateFormInitialValue = (data) => {
    if (!data) return;

    const formValues = {
      hospitalName: data.hospitalName || '',
      hospitalEmailId: data.hospitalEmailId || '',
      address: data?.address || '',
      zipCode: data?.zipCode || '',
      country: data?.country || '',
      state: data?.state || '',
      city: data?.city || '',
      hfrId: data.hfrId || '',
      website: data.website || '',
      contactNumberCountryCode: data.contactNumberCountryCode || '+91',
      contactNumber: data.contactNumber || '',
      whatsappCountryCode: data.whatsappCountryCode || '+91',
      whatsappNumber: data.whatsapp || '',
      emergencyContactNumberCountryCode: data.emergencyContactNumberCountryCode || '+91',
      emergencyContactNumber: data.emergencyContactNumber || '',
      numberOfHealthcareProfessionals: data.noOfHealthCareProfessional || '',
      specialitites: data.specialitites
        ? data.specialitites.map((item) => ({
            value: item,
            label: item === 'Multi specality hospital' ? 'Multispeciality Hospital' : item,
          }))
        : [],
      signature: data.user?.signature || '',
      superAdminName: data.user?.name || '',
      superAdminEmail: data.user?.email || '',
      superAdminContactNumber: data.user?.contactNumber || '',
      superAdminContactNumberCountryCode: data.user?.countryCode || '+91',
      superAdminWhatsappNumber: data.user?.whatsapp || '',
      superAdminWhatsappNumberCountryCode: data.user?.whatsappCountryCode || '+91',
      sameAsContact: data.sameAsContact || false,
      superAdminSameAsContact: data.user?.superAdminSameAsContact || false,
      telepathology: data.telepathology || false,
      teleradiology: data.teleradiology || false,
    };

    setFormInitialValue(formValues);
  };

  return (
    <>
      <div className="hospital page page__full" style={isDarkMode ? { background: '#090c29' } : customStyle.mainBackground}>
        {isSuperAdminMyProfile ? (
          <Header isBack={false} title="Master Superadmin Profile" />
        ) : (
          <Header isBack={true} title="Add Clinic or Hospital" />
        )}

        <div className="page__body">
          {/* {role !== 'Superadmin' && (
            <div className="subscriptionWrap">
              <h2 className="title title--h3 title--semibold">Subscription Details</h2>
              <div className="subscriptionWrap__row">
                <div className="subscriptionWrap__row__col">
                  <div className="card">
                    <div className="card__head">Plan Types</div>
                    <div className="card__body">
                      <h2 className="title title--semibold">Basic</h2>
                      <div className="btn__group">
                        <button className="btn btn__primary">Upgrade Plan</button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="subscriptionWrap__row__col">
                  <div className="card">
                    <div className="card__head">Plan Expiry Date</div>
                    <div className="card__body">
                      <h2 className="title title--semibold">12/12/2024</h2>
                      <div className="btn__group">
                        <button className="btn">14 days left</button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="subscriptionWrap__row__col">
                  <div className="card">
                    <div className="card__head">Data Remaining</div>
                    <div className="card__body">
                      <h2 className="title title--semibold">
                        25.34 GB <sub>/30 GB</sub>
                      </h2>
                      <div className="btn__group">
                        <button className="btn btn__white btn--full">Buy Extra Storage</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )} */}

          <div className="details">
            <div className="form form-innersec">
              {isSuperAdminMyProfile && isHospitalSuperAdminEditing && (
                <section className="form__section">
                  <h1 className="form__section__title">Change Password</h1>
                  <div className="formRow formRow__4">
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label" htmlFor="currentPassword">
                          Current Password
                        </label>
                        <input
                          type="password"
                          className="form__input form__input--sm"
                          id="currentPassword"
                          name="currentPassword"
                          placeholder="Current Password"
                          value={password.currentPassword}
                          onChange={(event) => handlePasswordChange('currentPassword', event.target.value)}
                          autoComplete="off"
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label" htmlFor="newPassword">
                          New Password
                        </label>
                        <input
                          type="password"
                          className="form__input form__input--sm"
                          id="newPassword"
                          name="newPassword"
                          placeholder="New Password"
                          value={password.newPassword}
                          onChange={(event) => handlePasswordChange('newPassword', event.target.value)}
                          autoComplete="off"
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label" htmlFor="confirmPassword">
                          Confirm Password
                        </label>
                        <input
                          type="password"
                          className="form__input form__input--sm"
                          id="confirmPassword"
                          name="confirmPassword"
                          placeholder="Confirm Password"
                          value={password.confirmPassword}
                          onChange={(event) => handlePasswordChange('confirmPassword', event.target.value)}
                          autoComplete="off"
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label" htmlFor="">
                          &nbsp;
                        </label>
                        <button
                          className="btn btn__primary"
                          onClick={handlePasswordSubmit}
                          type="button"
                        >
                          Update Password
                        </button>
                      </div>
                    </div>
                    {errorMessage && (
                      <span className="text-error text-xs" style={{ color: 'red' }}>
                        {errorMessage}
                      </span>
                    )}
                  </div>
                </section>
              )}

              <Formik
                initialValues={formInitialValue}
                enableReinitialize
                validationSchema={hospitalFormSchema}
                onSubmit={handleSave}
              >
                {({ errors, values, handleChange, handleBlur, setFieldValue }) => (
                  <Form>
                    {!isSuperAdminMyProfile && (
                      <>
                        <section className="form__section">
                          {role === 'hospitalsuperadmin' ? (
                            <div className="flex justify-between items-center">
                              <h1 className="form__section__title">
                                {isHospitalSuperAdminEditing ? 'Edit Profile Details' : 'Profile Details'}
                              </h1>
                              {!isHospitalSuperAdminEditing && (
                                <button
                                  className="custom-button main-theme flex items-center"
                                  onClick={() => setIsHospitalSuperAdminEditing(true)}
                                >
                                  <img
                                    src={ICONS['edit']}
                                    alt="edit"
                                    style={{ filter: 'brightness(0) invert(1)' }}
                                    className="mr-2"
                                  />
                                  Edit
                                </button>
                              )}
                            </div>
                          ) : (
                            <h1 className="form__section__title">
                              {edit ? 'Edit' : 'Add'} Clinic or Hospital
                            </h1>
                          )}
                          <div className="formRow formRow__4">
                            <div className="formCol">
                              <div className="form__Field">
                                <label className="form__label" htmlFor="hospitalName">
                                  Name <span className="required-asterisk">*</span>
                                </label>
                                <input
                                  className="form__input form__input--sm"
                                  type="text"
                                  id="hospitalName"
                                  name="hospitalName"
                                  placeholder="Name"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.hospitalName}
                                  autoComplete="off"
                                  disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                />
                                <ErrorMessage name="hospitalName" component="div" className="error text-error" />
                              </div>
                            </div>
                            <div className="formCol">
                              <div className="form__Field">
                                <label className="form__label">
                                  Email ID <span className="required-asterisk">*</span>
                                </label>
                                <input
                                  className="form__input form__input--sm"
                                  type="text"
                                  id="hospitalEmailId"
                                  name="hospitalEmailId"
                                  placeholder="Email ID"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.hospitalEmailId}
                                  autoComplete="off"
                                  disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                />
                                <ErrorMessage name="hospitalEmailId" component="div" className="error text-error" />
                              </div>
                            </div>
                            <div className="formCol">
                              <div className="form__Field">
                                <label className="form__label" htmlFor="address">
                                  Address
                                </label>
                                <input
                                  className="form__input form__input--sm"
                                  type="text"
                                  id="address"
                                  name="address"
                                  placeholder="Address"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.address}
                                  autoComplete="off"
                                  disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                />
                              </div>
                            </div>
                            <div className="formCol">
                              <div className="form__Field">
                                <label className="form__label" htmlFor="hfrId">
                                  HFR ID
                                </label>
                                <input
                                  className="form__input form__input--sm"
                                  type="text"
                                  id="hfrId"
                                  name="hfrId"
                                  placeholder="HFR ID"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.hfrId}
                                  autoComplete="off"
                                  disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="formRow formRow__4">
                            <div className="formCol">
                              <div className="form__Field">
                                <label className="form__label" htmlFor="zipCode">
                                  Zip Code
                                </label>
                                <input
                                  className="form__input form__input--sm"
                                  type="text"
                                  id="zipCode"
                                  name="zipCode"
                                  placeholder="Zip Code"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.zipCode}
                                  autoComplete="off"
                                  disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                />
                                <ErrorMessage name="zipCode" component="div" className="error text-error" />
                              </div>
                            </div>
                            <div className="formCol">
                              <div className="form__Field">
                                <label className="form__label" htmlFor="country">
                                  Country
                                </label>
                                <select
                                  id="country"
                                  name="country"
                                  className={`${role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing ? 'input-disabled' : ''} form__select form__select--sm`}
                                  onChange={(e) => {
                                    handleChange(e);
                                    fetchStates(e.target.value, '', 'state');
                                  }}
                                  onBlur={handleBlur}
                                  value={values.country}
                                  autoComplete="off"
                                  disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                  style={customStyle.select}
                                >
                                  <option value="">Select</option>
                                  {countryList.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="formCol">
                              <div className="form__Field">
                                <label className="form__label" htmlFor="state">
                                  State
                                </label>
                                <select
                                  id="state"
                                  name="state"
                                  className={`${role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing ? 'input-disabled' : ''} form__select form__select--sm`}
                                  onChange={(e) => {
                                    handleChange(e);
                                    fetchStates(values.country, e.target.value, 'city');
                                  }}
                                  onBlur={handleBlur}
                                  value={values.state}
                                  autoComplete="off"
                                  disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                  style={customStyle.select}
                                >
                                  <option value="">Select</option>
                                  {states.map((option) => (
                                    <option key={option.isoCode} value={option.isoCode}>
                                      {option.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="formCol">
                              <div className="form__Field">
                                <label className="form__label" htmlFor="city">
                                  City
                                </label>
                                <select
                                  id="city"
                                  name="city"
                                  className={`${role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing ? 'input-disabled' : ''} form__select form__select--sm`}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.city}
                                  autoComplete="off"
                                  disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                  style={customStyle.select}
                                >
                                  <option value="">Select</option>
                                  {cities.map((option) => (
                                    <option key={option.name} value={option.name}>
                                      {option.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                          <div className="formRow formRow__4">
                            <div className="formCol">
                              <div className="form__Field">
                                <label className="form__label" htmlFor="contactNumberCountryCode">
                                  Contact Number <span className="required-asterisk">*</span>
                                </label>
                                <div className="form__groupField form__groupField--phonenumber">
                                  <select
                                    className="form__select form__select--sm"
                                    style={customStyle.select}
                                    id="contactNumberCountryCode"
                                    name="contactNumberCountryCode"
                                    disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                    value={values.contactNumberCountryCode}
                                    onChange={(e) => {
                                      handleChange(e);
                                      if (values.sameAsContact) {
                                        setFieldValue('whatsappCountryCode', e.target.value);
                                        setFieldValue('whatsappNumber', values?.contactNumber);
                                      }
                                    }}
                                  >
                                    {countryCode.map((option) => (
                                      <option value={option}>{option}</option>
                                    ))}
                                  </select>
                                  <input
                                    type="text"
                                    className="form__input form__input--sm"
                                    id="contactNumber"
                                    name="contactNumber"
                                    placeholder="Contact Number"
                                    maxLength={10}
                                    onChange={(e) => {
                                      handleChange(e);
                                      if (values.sameAsContact) {
                                        setFieldValue('whatsappNumber', e.target.value);
                                        setFieldValue('whatsappCountryCode', values?.contactNumberCountryCode);
                                      }
                                    }}
                                    onBlur={handleBlur}
                                    value={values.contactNumber}
                                    autoComplete="off"
                                    disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                  />
                                </div>
                                <ErrorMessage name="contactNumber" component="div" className="error text-error" />
                              </div>
                            </div>
                            <div className="formCol">
                              <div className="form__Field">
                                <label className="form__label" htmlFor="whatsappNumber">
                                  Whatsapp Number
                                </label>
                                <div className="form__groupField form__groupField--phonenumber">
                                  <select
                                    className="form__select form__select--sm"
                                    style={customStyle.select}
                                    name="whatsappCountryCode"
                                    disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                    value={values.whatsappCountryCode}
                                    onChange={handleChange}
                                  >
                                    {countryCode.map((option) => (
                                      <option value={option}>{option}</option>
                                    ))}
                                  </select>
                                  <input
                                    type="text"
                                    className="form__input form__input--sm"
                                    id="whatsappNumber"
                                    name="whatsappNumber"
                                    maxLength={10}
                                    placeholder="Whatsapp Number"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.whatsappNumber}
                                    autoComplete="off"
                                    disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                  />
                                </div>
                                <div className="form__checkboxSubField">
                                  <div className="form__checkbox">
                                    <div className="form__checkbox__option m_0">
                                      <input
                                        className="form__checkbox__input"
                                        id="sameAsContact"
                                        disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                        checked={
                                          values.contactNumber &&
                                          values.whatsappNumber &&
                                          values.contactNumber === values.whatsappNumber
                                        }
                                        type="checkbox"
                                        name="sameAsContact"
                                        value="sameAsContact"
                                        onChange={(e) => {
                                          setFieldValue('sameAsContact', e.target.checked);
                                          if (e.target.checked) {
                                            setFieldValue('whatsappNumber', values.contactNumber);
                                            setFieldValue('whatsappCountryCode', values?.contactNumberCountryCode);
                                          } else {
                                            setFieldValue('whatsappNumber', '');
                                            setFieldValue('whatsappCountryCode', '');
                                          }
                                        }}
                                      />
                                      <label htmlFor="sameAsContact" className="form__checkbox__label">
                                        Same as Contact Number
                                      </label>
                                    </div>
                                  </div>
                                </div>
                                <ErrorMessage name="whatsappNumber" component="div" className="error text-error" />
                              </div>
                            </div>
                            <div className="formCol">
                              <div className="form__Field">
                                <label className="form__label" htmlFor="emergencyContactNumberCountryCode">
                                  Emergency Contact Number
                                </label>
                                <div className="form__groupField form__groupField--phonenumber">
                                  <select
                                    className="form__select form__select--sm"
                                    style={customStyle.select}
                                    id="emergencyContactNumberCountryCode"
                                    value={values.emergencyContactNumberCountryCode}
                                    disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                    onChange={handleChange}
                                  >
                                    {countryCode.map((option) => (
                                      <option value={option}>{option}</option>
                                    ))}
                                  </select>
                                  <input
                                    type="text"
                                    className="form__input form__input--sm"
                                    id="emergencyContactNumber"
                                    name="emergencyContactNumber"
                                    placeholder="Contact Number"
                                    maxLength={10}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.emergencyContactNumber}
                                    autoComplete="off"
                                    disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="formCol">
                              <div className="form__Field">
                                <label className="form__label" htmlFor="numberOfHealthcareProfessionals">
                                  No of Healthcare Professionals
                                </label>
                                <input
                                  type="number"
                                  className="form__input form__input--sm"
                                  id="numberOfHealthcareProfessionals"
                                  name="numberOfHealthcareProfessionals"
                                  min="0"
                                  step="1"
                                  placeholder="--"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.numberOfHealthcareProfessionals}
                                  autoComplete="off"
                                  disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="formRow formRow__4">
                            <div className="formCol">
                              <div className="form__Field">
                                <label className="form__label" htmlFor="website">
                                  Website
                                </label>
                                <input
                                  className="form__input form__input--sm"
                                  type="text"
                                  id="website"
                                  placeholder="Website"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.website}
                                  autoComplete="off"
                                  disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                />
                              </div>
                            </div>
                            <div className="formCol">
                              <div className="form__Field">
                                <label className="form__label" htmlFor="specialitites">
                                  Specialties <span className="required-asterisk">*</span>
                                </label>
                                <Select
                                  id="specialitites"
                                  placeholder="Select"
                                  className={`${role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing ? 'input-disabled' : ''} cutom-bg-white`}
                                  isMulti={true}
                                  isClearable={false}
                                  isSearchable={false}
                                  closeMenuOnSelect={false}
                                  hideSelectedOptions={false}
                                  isDisabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                  options={specialitites}
                                  value={values.specialitites}
                                  onChange={(selectedOptions) => {
                                    const newSpecialties = selectedOptions ? selectedOptions.map((option) => option) : [];
                                    setFieldValue('specialitites', newSpecialties);
                                  }}
                                />
                                <ErrorMessage name="specialitites" component="div" className="error text-error" />
                              </div>
                            </div>
                          </div>
                        </section>

                        <section className="form__section">
                          <h1 className="form__section__title">Service Details</h1>
                          <div className="formRow formRow__4">
                            <div className="formCol">
                              <div className="form__Field">
                                <label className="form__label" htmlFor="services">
                                  Services Needed
                                </label>
                                <div className="form__checkbox">
                                  <div className="form__checkbox__option mb_0">
                                    <input
                                      type="checkbox"
                                      name="telepathology"
                                      id="telepathology"
                                      checked={values.telepathology}
                                      onChange={(e) => setFieldValue('telepathology', e.target.checked)}
                                      className="form__checkbox__input"
                                      disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                    />
                                    <label className="form__checkbox__label" htmlFor="telepathology">
                                      Telepathology
                                    </label>
                                  </div>
                                  <div className="form__checkbox__option mb_0">
                                    <input
                                      type="checkbox"
                                      name="teleradiology"
                                      id="teleradiology"
                                      checked={values.teleradiology}
                                      onChange={(e) => setFieldValue('teleradiology', e.target.checked)}
                                      className="form__checkbox__input"
                                      disabled={role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing}
                                    />
                                    <label className="form__checkbox__label" htmlFor="teleradiology">
                                      Teleradiology
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </section>
                      </>
                    )}
                    {localStorage.getItem('role') === "hospitalsuperadmin" || sessionStorage.getItem('role') === "hospitalsuperadmin" && isHospitalSuperAdminEditing &&
                      <section className='form__section'>
                        <h1 className='form__section__title'>Change Password</h1>
                        <div className='formRow formRow__4'>
                          <div className='formCol'>
                            <div className="form__Field">
                              <label className="form__label" htmlFor='currentPassword'>
                                Current Password
                              </label>
                              <input
                                type="text"
                                className='form__input form__input--sm'
                                id="currentPassword"
                                name="currentPassword"
                                placeholder="Current Password"
                                value={password.currentPassword}
                                onChange={
                                  (event) => handlePasswordChange('currentPassword', event.target.value)
                                }
                                autoComplete='off'
                              />
                            </div>
                          </div>
                          <div className='formCol'>
                            <div className="form__Field">
                              <label className="form__label" htmlFor='newPassword'>
                                New Password
                              </label>
                              <input
                                type="text"
                                className='form__input form__input--sm'
                                id="newPassword"
                                name="newPassword"
                                placeholder="New Password"
                                value={password.newPassword}
                                onChange={
                                  (event) => handlePasswordChange('newPassword', event.target.value)
                                }
                                autoComplete='off'
                              />
                            </div>
                          </div>
                          <div className='formCol'>
                            <div className="form__Field">
                              <label className="form__label" htmlFor='confirmPassword'>
                                Confirm Password
                              </label>
                              <input
                                type="text"
                                className='form__input form__input--sm'
                                id="confirmPassword"
                                name="confirmPassword"
                                placeholder="Confirm Password"
                                value={password.confirmPassword}
                                onChange={
                                  (event) => handlePasswordChange('confirmPassword', event.target.value)
                                }
                                autoComplete='off'
                              />
                            </div>
                          </div>
                          <div className='formCol'>
                            <div className="form__Field">
                              <label className="form__label" htmlFor="">&nbsp;</label>
                              <span
                                className='btn btn__primary'
                                onClick={() => {
                                  setTitle("Update Password")
                                  setConfirmationText("Are you sure you want update your password?")
                                  setConfirmationModal(true)
                                }}
                              >
                                Update Password
                              </span>
                            </div>
                          </div>
                          {errorMessage && <span className='text-error text-xs' style={{ color: 'red' }}>{errorMessage}</span>}
                        </div>
                      </section>
                    }

{isSuperAdminMyProfile ? (
                    <section className="form__section">
                      <div className="flex justify-between items-center mb-4">
                        <h1 className="form__section__title">Super Admin Details</h1>
                        {isSuperAdminMyProfile && !isHospitalSuperAdminEditing && (
                          <button
                            className="custom-button main-theme flex items-center"
                            onClick={() => setIsHospitalSuperAdminEditing(true)}
                          >
                            <img
                              src={ICONS['edit']}
                              alt="edit"
                              style={{ filter: 'brightness(0) invert(1)' }}
                              className="mr-2"
                            />
                            Edit
                          </button>
                        )}
                      </div>
                      <div className="formRow formRow__4">
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label" htmlFor="superAdminName">
                              Name <span className="required-asterisk">*</span>
                            </label>
                            <input
                              type="text"
                              className="form__input form__input--sm"
                              id="superAdminName"
                              name="superAdminName"
                              placeholder="Name"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.superAdminName}
                              autoComplete="off"
                              disabled={(role === 'Superadmin' || role === 'hospitalsuperadmin') && !isHospitalSuperAdminEditing}
                            />
                            <ErrorMessage name="superAdminName" component="div" className="error text-error" />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label" htmlFor="superAdminEmail">
                              Email ID <span className="required-asterisk">*</span>
                            </label>
                            <input
                              type="text"
                              className="form__input form__input--sm"
                              id="superAdminEmail"
                              name="superAdminEmail"
                              placeholder="Email ID"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.superAdminEmail}
                              autoComplete="off"
                              disabled={role === 'Superadmin' || (role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing)}
                            />
                            <ErrorMessage name="superAdminEmail" component="div" className="error text-error" />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label" htmlFor="superAdminContactNumberCountryCode">
                              Contact Number <span className="required-asterisk">*</span>
                            </label>
                            <div className="form__groupField form__groupField--phonenumber">
                              <select
                                className="form__select form__select--sm"
                                style={customStyle.select}
                                id="superAdminContactNumberCountryCode"
                                disabled={(role === 'hospitalsuperadmin' || role === 'Superadmin') && !isHospitalSuperAdminEditing}
                                value={values.superAdminContactNumberCountryCode}
                                onChange={(e) => {
                                  setFieldValue('superAdminContactNumberCountryCode', e.target.value);
                                  if (values.superAdminSameAsContact) {
                                    setFieldValue('superAdminWhatsappNumber', values.superAdminContactNumber);
                                    setFieldValue('superAdminWhatsappNumberCountryCode', e.target.value);
                                  }
                                }}
                              >
                                {countryCode.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="text"
                                className="form__input form__input--sm"
                                id="superAdminContactNumber"
                                name="superAdminContactNumber"
                                placeholder="Contact Number"
                                disabled={(role === 'hospitalsuperadmin' || role === 'Superadmin') && !isHospitalSuperAdminEditing}
                                maxLength={10}
                                onInput={(e) => {
                                  e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                  handleChange(e);
                                }}
                                pattern="[0-9]*"
                                onChange={(e) => {
                                  setFieldValue('superAdminContactNumber', e.target.value);
                                  if (values.superAdminSameAsContact) {
                                    setFieldValue('superAdminWhatsappNumber', e.target.value);
                                    setFieldValue('superAdminWhatsappNumberCountryCode', values.superAdminContactNumberCountryCode);
                                  }
                                }}
                                onBlur={handleBlur}
                                value={values.superAdminContactNumber}
                              />
                            </div>
                            <ErrorMessage name="superAdminContactNumber" component="div" className="error text-error" />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label" htmlFor="superAdminWhatsappNumber">
                              Whatsapp Number <span className="required-asterisk">*</span>
                            </label>
                            <div className="form__groupField form__groupField--phonenumber">
                              <select
                                className="form__select form__select--sm"
                                style={customStyle.select}
                                id="superAdminWhatsappNumberCountryCode"
                                disabled={(role === 'hospitalsuperadmin' || role === 'Superadmin') && !isHospitalSuperAdminEditing}
                                value={values.superAdminWhatsappNumberCountryCode}
                                onChange={(e) => setFieldValue('superAdminWhatsappNumberCountryCode', e.target.value)}
                              >
                                {countryCode.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="text"
                                className="form__input form__input--sm"
                                id="superAdminWhatsappNumber"
                                name="superAdminWhatsappNumber"
                                placeholder="Whatsapp Number"
                                disabled={(role === 'hospitalsuperadmin' || role === 'Superadmin') && !isHospitalSuperAdminEditing}
                                maxLength={10}
                                onInput={(e) => {
                                  e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                  handleChange(e);
                                }}
                                pattern="[0-9]*"
                                onChange={(e) => {
                                  handleChange(e);
                                }}
                                onBlur={handleBlur}
                                value={values.superAdminWhatsappNumber}
                                autoComplete="off"
                              />
                            </div>
                            <div className="form__checkboxSubField">
                              <div className="form__checkbox">
                                <div className="form__checkbox__option m_0">
                                  <input
                                    className="form__checkbox__input"
                                    id="superAdminSameAsContact"
                                    disabled={(role === 'hospitalsuperadmin' || role === 'Superadmin') && !isHospitalSuperAdminEditing}
                                    checked={
                                      values.superAdminContactNumber &&
                                      values.superAdminWhatsappNumber &&
                                      values.superAdminContactNumber === values.superAdminWhatsappNumber
                                    }
                                    type="checkbox"
                                    name="superAdminSameAsContact"
                                    onChange={(e) => {
                                      setFieldValue('superAdminSameAsContact', e.target.checked);
                                      if (e.target.checked) {
                                        setFieldValue('superAdminWhatsappNumber', values.superAdminContactNumber);
                                        setFieldValue('superAdminWhatsappNumberCountryCode', values.superAdminContactNumberCountryCode);
                                      } else {
                                        setFieldValue('superAdminWhatsappNumber', '');
                                        setFieldValue('superAdminWhatsappNumberCountryCode', '');
                                      }
                                    }}
                                  />
                                  <label htmlFor="superAdminSameAsContact" className="form__checkbox__label">
                                    Same as Contact Number
                                  </label>
                                </div>
                              </div>
                            </div>
                            <ErrorMessage name="superAdminWhatsappNumber" component="div" className="error text-error" />
                          </div>
                        </div>
                      </div>
                      {role !== 'Superadmin' && (
                        <div className="formRow">
                          <div className="formCol">
                            <div className="form__Field">
                              {isHospitalSuperAdminEditing && (
                                <label className="form__label" htmlFor="signature">
                                  Add/Upload Signature
                                  {role !== 'Front Desk' && <span className="required-asterisk">*</span>}
                                </label>
                              )}
                              <div className="relative">
                                {!isHospitalSuperAdminEditing && (
                                  <div className="absolute inset-0 z-10 cursor-not-allowed bg-transparent" />
                                )}
                                <div
                                  className={`relative z-0 ${
                                    !isHospitalSuperAdminEditing ? 'opacity-50 pointer-events-none' : 'cursor-pointer'
                                  }`}
                                >
                                  <SignatureDropzone
                                    onDrop={(acceptedFiles) => onDrop(acceptedFiles, setFieldValue)}
                                    preview={values?.signature}
                                    accept={{ 'image/jpeg': ['.jpeg', '.jpg'], 'image/png': ['.png'] }}
                                  />
                                </div>
                              </div>
                              {isHospitalSuperAdminEditing && (
                                <>
                                  <div>
                                    <p className="addSignatureNote" style={{ color: isDarkMode ? '#ffffff' : '#a00000' }}> Note: Please use a black pen and white paper to sign </p>
                                    <p className="addSignatureDiv -mt-2">Or</p>
                                  </div>
                                  <div className="addSignatureDiv">
                                    <button
                                      className="btn btn__white"
                                      type="button"
                                      onClick={openSignaturePad}
                                      style={!isHospitalSuperAdminEditing ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                    >
                                      Add Signature
                                    </button>
                                  </div>
                                </>
                              )}
                              <ErrorMessage name="signature" component="div" className="error text-error" />
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="btn__group">
                        {(role === 'hospitalsuperadmin' || role === 'Superadmin') ? (
                          isHospitalSuperAdminEditing && (
                            <>
                              <button className="btn btn__primary" type="submit">
                                Update
                              </button>
                              <button
                                className="btn btn__white"
                                onClick={() => {
                                  cancel();
                                  setIsHospitalSuperAdminEditing(false);
                                }}
                              >
                                Cancel
                              </button>
                            </>
                          )
                        ) : (
                          <>
                            <button className="btn btn__primary" type="submit">
                              {edit ? 'Update' : 'Save'}
                            </button>
                            {!isSuperAdminMyProfile && !['hospitalsuperadmin', 'Superadmin'].includes(role) && (
                              <button className="mr-2 custom-button" type="button" onClick={cancel}>
                                Cancel
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </section>
                  ) : (
                    <section className="form__section">
                    <div className="flex justify-between items-center mb-4">
                      <h1 className="form__section__title">Super Admin Details</h1>
                      {role === 'Superadmin' && !isHospitalSuperAdminEditing && (
                        <button
                          className="custom-button main-theme flex items-center"
                          onClick={() => setIsHospitalSuperAdminEditing(true)}
                        >
                          <img
                            src={ICONS['edit']}
                            alt="edit"
                            style={{ filter: 'brightness(0) invert(1)' }}
                            className="mr-2"
                          />
                          Edit
                        </button>
                      )}
                    </div>
                    <div className="formRow formRow__4">
                      <div className="formCol">
                        <div className="form__Field">
                          <label className="form__label" htmlFor="superAdminName">
                            Name <span className="required-asterisk">*</span>
                          </label>
                          <input
                            type="text"
                            className="form__input form__input--sm"
                            id="superAdminName"
                            name="superAdminName"
                            placeholder="Name"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.superAdminName}
                            autoComplete="off"
                            disabled={(role === 'Superadmin' || role === 'hospitalsuperadmin') && !isHospitalSuperAdminEditing}
                          />
                          <ErrorMessage name="superAdminName" component="div" className="error text-error" />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label className="form__label" htmlFor="superAdminEmail">
                            Email ID <span className="required-asterisk">*</span>
                          </label>
                          <input
                            type="text"
                            className="form__input form__input--sm"
                            id="superAdminEmail"
                            name="superAdminEmail"
                            placeholder="Email ID"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.superAdminEmail}
                            autoComplete="off"
                            disabled={role === 'Superadmin' || (role === 'hospitalsuperadmin' && !isHospitalSuperAdminEditing)}
                          />
                          <ErrorMessage name="superAdminEmail" component="div" className="error text-error" />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label className="form__label" htmlFor="superAdminContactNumberCountryCode">
                            Contact Number <span className="required-asterisk">*</span>
                          </label>
                          <div className="form__groupField form__groupField--phonenumber">
                            <select
                              className="form__select form__select--sm"
                              style={customStyle.select}
                              id="superAdminContactNumberCountryCode"
                              disabled={(role === 'hospitalsuperadmin' || role === 'Superadmin') && !isHospitalSuperAdminEditing}
                              value={values.superAdminContactNumberCountryCode}
                              onChange={(e) => {
                                setFieldValue('superAdminContactNumberCountryCode', e.target.value);
                                if (values.superAdminSameAsContact) {
                                  setFieldValue('superAdminWhatsappNumber', values.superAdminContactNumber);
                                  setFieldValue('superAdminWhatsappNumberCountryCode', e.target.value);
                                }
                              }}
                            >
                              {countryCode.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                            <input
                              type="text"
                              className="form__input form__input--sm"
                              id="superAdminContactNumber"
                              name="superAdminContactNumber"
                              placeholder="Contact Number"
                              disabled={(role === 'hospitalsuperadmin' || role === 'Superadmin') && !isHospitalSuperAdminEditing}
                              maxLength={10}
                              onChange={(e) => {
                                setFieldValue('superAdminContactNumber', e.target.value);
                                if (values.superAdminSameAsContact) {
                                  setFieldValue('superAdminWhatsappNumber', e.target.value);
                                  setFieldValue('superAdminWhatsappNumberCountryCode', values.superAdminContactNumberCountryCode);
                                }
                              }}
                              onBlur={handleBlur}
                              value={values.superAdminContactNumber}
                              autoComplete="off"
                            />
                          </div>
                          <ErrorMessage name="superAdminContactNumber" component="div" className="error text-error" />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label className="form__label" htmlFor="superAdminWhatsappNumber">
                            Whatsapp Number <span className="required-asterisk">*</span>
                          </label>
                          <div className="form__groupField form__groupField--phonenumber">
                            <select
                              className="form__select form__select--sm"
                              style={customStyle.select}
                              id="superAdminWhatsappNumberCountryCode"
                              disabled={(role === 'hospitalsuperadmin' || role === 'Superadmin') && !isHospitalSuperAdminEditing}
                              value={values.superAdminWhatsappNumberCountryCode}
                              onChange={(e) => setFieldValue('superAdminWhatsappNumberCountryCode', e.target.value)}
                            >
                              {countryCode.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                            <input
                              type="text"
                              className="form__input form__input--sm"
                              id="superAdminWhatsappNumber"
                              name="superAdminWhatsappNumber"
                              placeholder="Whatsapp Number"
                              disabled={(role === 'hospitalsuperadmin' || role === 'Superadmin') && !isHospitalSuperAdminEditing}
                              maxLength={10}
                              onInput={(e) => {
                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                handleChange(e);
                              }}
                              pattern="[0-9]*"
                              onChange={(e) => setFieldValue('superAdminWhatsappNumber', e.target.value)}
                              onBlur={handleBlur}
                              value={values.superAdminWhatsappNumber}
                              autoComplete="off"
                            />
                          </div>
                          <div className="form__checkboxSubField">
                            <div className="form__checkbox">
                              <div className="form__checkbox__option m_0">
                                <input
                                  className="form__checkbox__input"
                                  id="superAdminSameAsContact"
                                  disabled={(role === 'hospitalsuperadmin' || role === 'Superadmin') && !isHospitalSuperAdminEditing}
                                  checked={
                                    values.superAdminContactNumber &&
                                    values.superAdminWhatsappNumber &&
                                    values.superAdminContactNumber === values.superAdminWhatsappNumber
                                  }
                                  type="checkbox"
                                  name="superAdminSameAsContact"
                                  onChange={(e) => {
                                    setFieldValue('superAdminSameAsContact', e.target.checked);
                                    if (e.target.checked) {
                                      setFieldValue('superAdminWhatsappNumber', values.superAdminContactNumber);
                                      setFieldValue('superAdminWhatsappNumberCountryCode', values.superAdminContactNumberCountryCode);
                                    } else {
                                      setFieldValue('superAdminWhatsappNumber', '');
                                      setFieldValue('superAdminWhatsappNumberCountryCode', '');
                                    }
                                  }}
                                />
                                <label htmlFor="superAdminSameAsContact" className="form__checkbox__label">
                                  Same as Contact Number
                                </label>
                              </div>
                            </div>
                          </div>
                          <ErrorMessage name="superAdminWhatsappNumber" component="div" className="error text-error" />
                        </div>
                      </div>
                    </div>
                    {role !== 'Superadmin' && (
                      <div className="formRow">
                        <div className="formCol">
                          <div className="form__Field">
                            {isHospitalSuperAdminEditing && (
                              <label className="form__label" htmlFor="signature">
                                Add/Upload Signature
                                {role !== 'Front Desk' && <span className="required-asterisk">*</span>}
                              </label>
                            )}
                            <div className="relative">
                              {!isHospitalSuperAdminEditing && (
                                <div className="absolute inset-0 z-10 cursor-not-allowed bg-transparent" />
                              )}
                              <div
                                className={`relative z-0 ${
                                  !isHospitalSuperAdminEditing ? 'opacity-50 pointer-events-none' : 'cursor-pointer'
                                }`}
                              >
                                <SignatureDropzone
                                  onDrop={(acceptedFiles) => onDrop(acceptedFiles, setFieldValue)}
                                  preview={values?.signature}
                                  accept={{ 'image/jpeg': ['.jpeg', '.jpg'], 'image/png': ['.png'] }}
                                />
                              </div>
                            </div>
                            {isHospitalSuperAdminEditing && (
                              <>
                                <div>
                                  <p className="addSignatureNote" style={{ color: isDarkMode ? '#ffffff' : '#a00000' }}> Note: Please use a black pen and white paper to sign </p>
                                  <p className="addSignatureDiv -mt-2">Or</p>
                                </div>
                                <div className="addSignatureDiv">
                                  <button
                                    className="btn btn__white"
                                    type="button"
                                    onClick={openSignaturePad}
                                    style={!isHospitalSuperAdminEditing ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                  >
                                    Add Signature
                                  </button>
                                </div>
                              </>
                            )}
                            <ErrorMessage name="signature" component="div" className="error text-error" />
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="btn__group">
                      {(role === 'hospitalsuperadmin' || role === 'Superadmin') ? (
                        isHospitalSuperAdminEditing && (
                          <>
                            <button className="btn btn__primary" type="submit">
                              Update
                            </button>
                            <button
                              className="btn btn__white"
                              onClick={() => {
                                setIsHospitalSuperAdminEditing(false);
                                if (role === 'Superadmin') {
                                  dispatch(getSuperadminDetailsApi());
                                  setIsHospitalSuperAdminEditing(false);
                                }
                              }}
                            >
                              Cancel
                            </button>
                          </>
                        )
                      ) : (
                        <>
                          <button className="btn btn__primary" type="submit">
                            {edit ? 'Update' : 'Save'}
                          </button>
                          {!isSuperAdminMyProfile && !['hospitalsuperadmin', 'Superadmin'].includes(role) && (
                            <button className="mr-2 custom-button" type="button" onClick={cancel}>
                              Cancel
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </section>
                  )}
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationPopup
        title={title}
        message={confirmationText}
        onConfirm={confirmPasswordChange}
        onCancel={() => {
          setTitle('');
          setConfirmationText('');
          setConfirmationModal(false);
        }}
        isOpen={confirmationModal}
      />
      <SignaturePad
        title={title}
        message={confirmationText}
        onConfirm={saveSignature}
        onCancel={() => {
          setOpenSignaturePad(false);
        }}
        isOpen={isOpenSignaturePad}
      />
      <SuccessModal isSuccessModalOpen={modal} title={title} handleCloseModal={handleCloseModal} />
      <Loader isLoaderOpen={hospitalDetailsData?.loading || loading} title="Loading..." />
    </>
  );
};

export default myProfileHospital;